import type { ConvexReactClient } from 'convex/react';
import type { NavigationDraftCheckpoint } from './navigation-draft-checkpoint';
import type { Id } from '@/convex/_generated/dataModel';
import { api } from '@/lib/convex';
import type {
  OrgChatAttachmentInput,
  OrgChatPresetDataInput,
} from '@/components/org-chat/org-chat-types';
import type { OrgChatSendOptions } from '@/contexts/org-chat-interface-context';
import {
  dispatchThreadTargets,
  isSendAccepted,
} from '@/components/org-chat/org-chat-delivery';
import {
  getBrowserNavigationStorage,
  rememberPendingNavigationPrompt,
} from '@/components/chat/tool-calls/workspace-navigation-pending';
import { getConnectorQueueFields } from '@/components/org-chat/connector-queue-fields';
import { prepareSharedDispatchUploads } from '@/components/org-chat/org-chat-shared-dispatch-uploads';
import { buildBaseMessagePayload } from '@/components/org-chat/payload/build-base-payload';
import {
  buildAttachmentFields,
  buildPresetFields,
  mergeReferencePayloadFields,
  type OrgChatSendPayload,
} from '@/components/org-chat/org-chat-payload-builders';
import {
  designClosedArtifactPanelState,
  normalizeDesignTargetThreadRouteRefs,
} from './shared';
import {
  markClientMutationAck,
  markClientMutationDispatch,
} from '@/components/org-chat/client-visible-response-start';

export type DesignNavigationDelivery = {
  id: string;
  actorId: string;
  orgSlug: string;
  routeRef: string;
  orgId: Id<'organizations'>;
  title: string;
  visibility: 'organization' | 'private';
  projectId?: Id<'threadFolders'>;
  prompt: string;
  rawUserPrompt?: string;
  attachments?: OrgChatAttachmentInput[];
  presetData?: OrgChatPresetDataInput;
  options: OrgChatSendOptions;
  sharedFields?: Partial<OrgChatSendPayload>;
  sourceDraftCheckpoint?: NavigationDraftCheckpoint;
};

/** The unbound snapshot survives navigation; each attempt binds each destination once. */
export async function deliverDesignNavigation(
  job: DesignNavigationDelivery,
  client: Pick<ConvexReactClient, 'query' | 'mutation'>,
  checkpoint: (job: DesignNavigationDelivery) => void,
  assertActor: () => void = () => {}
) {
  assertActor();
  // Older persisted navigation jobs may predate the input timestamp.
  if (job.options.clientSendStartedAt === undefined) {
    job.options = { ...job.options, clientSendStartedAt: Date.now() };
    checkpoint(job);
  }
  const clientSendStartedAt = job.options.clientSendStartedAt;
  const attachmentFields = buildAttachmentFields(job.attachments);
  const rememberAcceptedPrompt = (
    routeRef: string,
    agentThreadId: string,
    result: unknown
  ) => {
    if (
      routeRef !== job.routeRef ||
      !isSendAccepted(result) ||
      !result ||
      typeof result !== 'object' ||
      !('messageId' in result) ||
      typeof result.messageId !== 'string'
    )
      return;
    const storage = getBrowserNavigationStorage();
    if (storage)
      rememberPendingNavigationPrompt(storage, agentThreadId, result.messageId);
  };
  if (!job.sharedFields) {
    job.sharedFields = await prepareSharedDispatchUploads(
      { ...attachmentFields, ...buildPresetFields(job.presetData) },
      (uploads) => {
        assertActor();
        return client.mutation(
          api.generations.mutations.upsertComposerUploadedFilesForCurrentUser,
          { orgSlug: job.orgSlug, uploads }
        );
      }
    );
    checkpoint(job);
  }
  return dispatchThreadTargets({
    existingRouteRefs: normalizeDesignTargetThreadRouteRefs(job.options),
    newRouteRefs: job.options.newThreadRouteRefs ?? [],
    options: job.options,
    resolve: async (routeRefs) => {
      assertActor();
      if (!routeRefs.length) return [];
      const entries: Array<{
        routeRef: string;
        thread: { _id: Id<'orgThreads'>; threadId: string } | null;
      }> = await client.query(
        api.organizations.threads.queries.getManyByRouteRefsForCurrentUser,
        { routeRefs }
      );
      return entries.flatMap((entry) =>
        entry.thread
          ? [
              {
                routeRef: entry.routeRef,
                threadId: {
                  orgThreadId: entry.thread._id,
                  agentThreadId: entry.thread.threadId,
                },
              },
            ]
          : []
      );
    },
    createAndSend: async (routeId) => {
      assertActor();
      const clientInputId = `${job.id}:${routeId}`;
      markClientMutationDispatch(clientInputId, performance.now());
      const receipt = await client.mutation(
        api.organizations.threads.create_and_send.createThreadAndSendMessage,
        {
          ...buildMessage(routeId),
          routeId,
          title: job.title,
          visibility: job.visibility,
          ...(job.projectId ? { projectId: job.projectId } : {}),
        }
      );
      markClientMutationAck(clientInputId, performance.now());
      rememberAcceptedPrompt(routeId, receipt.threadId, receipt.result);
      return receipt.result;
    },
    send: async ({ orgThreadId, agentThreadId }, routeRef) => {
      assertActor();
      const clientInputId = `${job.id}:${routeRef}`;
      markClientMutationDispatch(clientInputId, performance.now());
      const result = await client.mutation(
        api.products.ai.mutations.org_thread_send_message.sendMessage,
        { ...buildMessage(routeRef), orgThreadId }
      );
      markClientMutationAck(clientInputId, performance.now());
      rememberAcceptedPrompt(routeRef, agentThreadId, result);
      return result;
    },
  });

  function buildMessage(routeRef: string) {
    return mergeReferencePayloadFields(
      {
        ...buildBaseMessagePayload({
          orgId: job.orgId,
          prompt: job.prompt,
          rawUserPrompt: job.rawUserPrompt,
          forceDeepResearch: job.options.forceDeepResearch,
        }),
        ...job.sharedFields,
        // Persisted failed jobs can contain old catalog-only selection fields.
        ...getConnectorQueueFields(job.sharedFields?.selectedConnectors),
        artifactPanelState: designClosedArtifactPanelState(),
        deliveryMetadata: {
          deliveryKind: 'send' as const,
          clientInputId: `${job.id}:${routeRef}`,
          createdAt: clientSendStartedAt,
        },
      },
      attachmentFields
    );
  }
}
